import { BaseViewStore } from 'core/stores';
import FulfillmentOrderProcessingStore from './FulfillmentOrderProcessingStore';
import FulfillmentOrderStore from './FulfillmentOrderStore';
import { MarkAsShippedForm, MarkAsShippedLtlForm } from 'modules/order/forms';
import { action, makeObservable } from 'mobx';
import { ProductInventoryStore } from 'modules/product-inventory/stores';
import { getDistinctProductIds, translate, isSuperAdminOrganization } from 'core/utils';
import { CarrierServiceStore, CarrierStore } from 'modules/carrier/stores';
import { carrierTypes, shipmentInfoStatusEnum, sourceOfChange } from 'common/constants';
import { BaasicDropdownViewStore } from 'common/stores';
import _ from 'lodash';

class MarkAsShippedViewStore extends BaseViewStore {
	selectedItem = null;

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.initialize(_.get(this.modalParams, 'data.selectedItemId'));
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
	}

	@action.bound
	initialize = async selectedItemId => {
		this.suspend();
		await this.getFulfillmentOrder(selectedItemId);
		this.isLtlOrder =
				_.get(this.selectedItem, 'order.channel.name') === 'LTL Freight' ||
				_.get(this.selectedItem, 'order.requiredCarrierService') == 'LTL';
		

		if (this.isLtlOrder) {
			await this.initializeLtl();
		}
		else {
			await this.initializeNonLtl();
		}
		this.resume();
	};

	@action.bound
	initializeLtl = async () => {
		this.form = new MarkAsShippedLtlForm({
			onSuccess: async () => {
				await this.markAsShippedLtl();
				this.modalParams.success();
			},
		});
		this.updateForm();
		this.carrierDropdownStore = new BaasicDropdownViewStore();
		await this.carrierDropdownStore.initialize({
			field: this.form.$('carrierId'),
			store: new CarrierStore(this.rootStore),
			value: this.selectedItem.carrier,
			filterProperties: {
				sellerIds: [this.selectedItem.sellerId],
				carrierTypeNames: [carrierTypes.ltl]
			}
		})
	}

	@action.bound
	initializeNonLtl = async () => {
		this.form = new MarkAsShippedForm({
			onSuccess: async () => {
				await this.markAsShipped();
				this.modalParams.success();
			},
		});
		this.updateForm();
		
		this.warehouseDropdownStore = new BaasicDropdownViewStore();
		await this.warehouseDropdownStore.initialize({
			field: this.form.$('warehouseId'),
			store: new ProductInventoryStore(this.rootStore),
			value:
				this.selectedItem.warehouseOrganization != null
					? {
							value: this.selectedItem.warehouseOrganization.id,
							label: this.selectedItem.warehouseOrganization.applicationTitle,
							extra: this.selectedItem.warehouseOrganization,
					  }
					: null,
			itemTemplate: {
				value: 'assignedWarehouseId',
				label: 'assignedWarehouse.applicationTitle',
			},
			initCallback: this.checkAvailableWarehouse,
			filterProperties: {
				fulfillmentOrderId: this.selectedItem.id,
				productIds: getDistinctProductIds(this.selectedItem.fulfillmentOrderItems),
				calculateInventory: true,
				isOrganizationActive: true,
				filterInapplicableWarehouses: true,
				includeOnlySellerWarehouses: true,
				sellerWarehousesOnlyOrganizationId: this.selectedItem.sellerId,
				embed: [
					'assignedWarehouse',
					'assignedWarehouse.warehouseOrganizationInfo',
					'assignedWarehouse.warehouseOrganizationInfo.address',
					'assignedWarehouse.warehouseOrganizationInfo.address.coreCountry',
					'assignedWarehouse.warehouseOrganizationInfo.address.coreCountryState',
					'product',
					'product.multiPackageProductParts',
				],
			},
		});

		this.carrierServiceDropdownStore = new BaasicDropdownViewStore();
		await this.carrierServiceDropdownStore.initialize({
			field: this.form.$('carrierServiceId'),
			store: new CarrierServiceStore(this.rootStore),
			value: this.selectedItem.carrierService,
		});

		if (this.isSuperAdminOrganization == false && this.selectedItem.seller.sellerOrganizationInfo.showSurplusCharge == false) {
			this.shipmentInfo = _.get(this.selectedItem, 'shipmentInfos[0]');
			if (this.shipmentInfo) {
				this.form.$('cost').set(this.shipmentInfo.shippingCostTotal);
			}
		}
		
	}

	@action.bound
	getFulfillmentOrder = async selectedItemId => {
		this.selectedItem = await this.fulfillmentOrderStore.getFulfillmentOrder(selectedItemId, {
			embed: ['shipmentInfos', 'warehouseOrganization', 'carrierService', 'fulfillmentOrderItems', 'order', 'order.channel', 'carrier', 'seller', 'seller.sellerOrganizationInfo'],
		});
	};

	@action.bound
	updateForm = () => {
		const shipmentInfo = _.find(this.selectedItem.shipmentInfos, shipmentInfo => {
			return shipmentInfo.status == shipmentInfoStatusEnum.active;
		});
		if (shipmentInfo) {
			this.form.updateForm(shipmentInfo);
		}
	};

	@action.bound
	checkAvailableWarehouse = dropdownStore => {
		if (dropdownStore.value) {
			const matchingWarehouse = _.find(dropdownStore.options, option => {
				return option.value === dropdownStore.value.value;
			});
			// If the currently assigned warehouse does not have available inventory
			if (!matchingWarehouse) {
				dropdownStore.setValue({
					value: null,
					label: `${dropdownStore.value.label} ${translate(
						'FULFILLMENTORDER.MARKASSHIPPEDMODAL.NOAVAILABLEINVENTORY'
					)}`,
				});
			}
		}
	};

	@action.bound
	markAsShippedLtl = async () => {
		try {
			const { isInternal, trackingNumber, carrierId } = this.form.values();

			await new FulfillmentOrderProcessingStore(this.rootStore).markAsShippedLtl(
				{
					id: this.selectedItem.id,
					trackingNumber,
					isInternal,
					carrierId,
				},
				sourceOfChange.markAsShipped
			);
			this.rootStore.notificationStore.success([
				'FULFILLMENTORDER.MARKASSHIPPEDMODAL.SUCCESS',
				{ id: this.selectedItem.fulfillmentOrderId },
			]);
		}
		catch (error) {
			if (_.get(error, 'data.message') == 'FULFILLMENTORDER.ALREADYPROCESSING') {
				this.rootStore.notificationStore.error([
					error.data.message,
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			} else {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.MARKASSHIPPEDMODAL.FAIL',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			}
		}
	}

	@action.bound
	markAsShipped = async () => {
		try {
			let markAsShippedParams = {};
			const { isInternal, ...otherValues } = this.form.values();
			if (isInternal) {
				markAsShippedParams = { ...otherValues, isInternal };
			} else {
				markAsShippedParams = {
					trackingNumber: '',
					cost: null,
					shippingCostSurcharge: 0,
					warehouseId: null,
					carrierServiceId: null,
					isInternal,
				};
			}
			await new FulfillmentOrderProcessingStore(this.rootStore).markAsShipped(
				{
					id: this.selectedItem.id,
					...markAsShippedParams,
				},
				sourceOfChange.markAsShipped
			);
			this.rootStore.notificationStore.success([
				'FULFILLMENTORDER.MARKASSHIPPEDMODAL.SUCCESS',
				{ id: this.selectedItem.fulfillmentOrderId },
			]);
		} catch (err) {
			if (err.statusCode == 400 && _.get(err, 'data.message') == 'FULFILLMENTORDER.ALREADYPROCESSING') {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.ALREADYPROCESSING',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			} else {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.MARKASSHIPPEDMODAL.FAIL',
					{ id: this.selectedItem.fulfillmentOrderId },
				]);
			}
		}
	};

	@action.bound
	onSwitch = () => {
		const newValue = !this.form.$('isInternal').value;
		this.form.$('isInternal').set('value', newValue);
		
		if (this.isLtlOrder) {
			if (newValue) {
				this.form.setFieldRules(['trackingNumber', 'carrierId'], 'required');
			} else {
				this.form.clearRules(['trackingNumber', 'carrierId']);
			}
		}
		else {
			if (newValue) {
				this.form.setFieldRules(['trackingNumber', 'warehouseId', 'carrierServiceId'], 'required');
				this.form.$('cost').set('rules', 'required|numeric|decimalLength');
				this.form.$('shippingCostSurcharge').set('rules', 'numeric|decimalLength');
			} else {
				this.form.clearRules(['warehouseId', 'trackingNumber', 'carrierServiceId', 'cost', 'shippingCostSurcharge']);
			}
		}

	};

	@action.bound
	onKeyUp = (event, field) => {
		if (this.clearInputOnChange && !event.ctrlKey && /^[0-9\b]+$/.test(event.key)) {
			this.clearInputOnChange = false;
			field.value = event.currentTarget.value.slice(-1);
		} else {
			field.value = event.currentTarget.value.replace(/ /g, '');
		}
	};
}

export default MarkAsShippedViewStore;
